/**
 * Manual Entry Tab Component
 * Extracted from ManualEntryPage for use within DataEntryPage
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { FormField } from '../forms/FormField';
import { ServiceTypeSelect } from '../forms/ServiceTypeSelect';
import { EntryConfirmationModal } from '../forms/EntryConfirmationModal';
import { useManualEntryOperations } from '../../hooks/useApi';
import { useCurrencyCode } from '../../hooks/useCurrency';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import {
  calculateBagKilograms,
  formatBagQuantity,
  formatNumberEs,
  getAvailableUnits,
  getUnit,
} from '../../utils/formatters';
import { BAG_SIZE_OPTIONS } from '../../constants';
import type {
  BagSize,
  WasteServiceType,
  ManualConsumptionEntryPayload,
  BagWasteEntryPayload,
} from '../../types/api';

// ============================================================================
// Types
// ============================================================================

interface ManualEntryFormSchema {
  start_date: string;
  end_date: string;
  service_type:
    | 'electricidad'
    | 'agua'
    | 'gas'
    | 'residuos-reciclables'
    | 'residuos-organicos'
    | 'residuos-generales';
  consumption: number;
  consumption_unit: string;
  notes?: string;
  cost_total?: number;
  currency?: string;
  vendor_name?: string;
  invoice_number?: string;
  issued_date?: string;
  supply_id?: string;
  bag_size?: BagSize;
}

const isWasteServiceType = (
  value: ManualEntryFormSchema['service_type'],
): value is WasteServiceType => {
  return (
    value === 'residuos-reciclables' ||
    value === 'residuos-organicos' ||
    value === 'residuos-generales'
  );
};

type BagWastePayloadWithUnit = BagWasteEntryPayload & {
  consumption_unit: 'bolsas';
};

// ============================================================================
// Styled Components
// ============================================================================

const TabContainer = styled.div`
  width: 100%;
  max-width: 52rem;
  min-width: 0;
  margin: 0;
`;

const FormSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-md);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRangeContainer = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const ConsumptionFields = styled.div`
  display: grid;
  gap: 1rem;
`;

const UnitToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const UnitToggleOption = styled.label<{ $selected: boolean }>`
  border: 1px solid
    ${(props) => (props.$selected ? 'var(--brand-500)' : 'var(--border)')};
  border-radius: var(--radius-md);
  padding: 0.35rem 1rem;
  font-weight: 600;
  color: ${(props) =>
    props.$selected ? 'var(--brand-600)' : 'var(--text-secondary)'};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition:
    border-color 0.2s ease,
    color 0.2s ease;

  input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    accent-color: var(--action-primary);
  }
  &:focus-within {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
  min-height: 44px;
`;

const BagSizeGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const BagSizeCard = styled.label<{ $selected: boolean }>`
  border: 2px solid
    ${(props) => (props.$selected ? 'var(--brand-400)' : 'var(--border)')};
  border-radius: var(--radius-md);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: var(--brand-300);
  }

  input {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
    accent-color: var(--action-primary);
  }
  &:focus-within {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 3px;
  }
  min-height: 44px;

  strong {
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  span {
    color: var(--text-muted);
    font-size: 0.85rem;
  }
`;

const BagPreviewCard = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: var(--radius-md);
  border: 1px dashed var(--brand-200);
  background: var(--brand-50);
  color: var(--brand-800);
`;

const BagPreviewTitle = styled.p`
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

const ErrorText = styled.p`
  color: var(--danger-600);
  font-size: 0.85rem;
  margin-top: 0.35rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const OptionalSection = styled.div`
  background: var(--surface-subtle);
  padding: var(--space-lg);
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
`;

const OptionalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: var(--space-md);
`;

const OptionalTitle = styled.h4`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  margin: 0;
`;

const OptionalFields = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

// ============================================================================
// Validation Schema
// ============================================================================

const manualEntrySchema = yup.object({
  start_date: yup
    .string()
    .required('La fecha de inicio es requerida')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido'),
  end_date: yup
    .string()
    .required('La fecha de fin es requerida')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido'),
  service_type: yup
    .string()
    .oneOf(
      [
        'electricidad',
        'agua',
        'gas',
        'residuos-reciclables',
        'residuos-organicos',
        'residuos-generales',
      ],
      'El tipo de servicio es requerido',
    )
    .required('El tipo de servicio es requerido'),
  consumption: yup
    .number()
    .typeError('El consumo es requerido')
    .when('consumption_unit', {
      is: (value: string) => value === 'bolsas',
      then: (schema) =>
        schema
          .integer('Las bolsas deben ser un número entero')
          .min(1, 'Debe registrar al menos 1 bolsa')
          .max(10000, 'El número de bolsas es demasiado alto'),
      otherwise: (schema) =>
        schema
          .min(0, 'El consumo debe ser mayor o igual a 0')
          .max(1000000, 'El consumo parece demasiado alto'),
    })
    .required('El consumo es requerido'),
  consumption_unit: yup.string().required(),
  bag_size: yup
    .string<BagSize>()
    .oneOf(['small', 'medium', 'big'], 'Selecciona un tamaño de bolsa válido')
    .when('consumption_unit', {
      is: (value: string) => value === 'bolsas',
      then: (schema) => schema.required('Selecciona un tamaño de bolsa'),
      otherwise: (schema) => schema.optional(),
    }),
  notes: yup
    .string()
    .max(500, 'Las notas no pueden superar los 500 caracteres')
    .optional(),
  cost_total: yup
    .number()
    .min(0, 'El costo debe ser mayor o igual a 0')
    .optional(),
  currency: yup.string().optional(),
  vendor_name: yup.string().optional(),
  invoice_number: yup.string().optional(),
  issued_date: yup.string().optional(),
  supply_id: yup.string().optional(),
});

// ============================================================================
// Component
// ============================================================================

export const ManualEntryTab: React.FC = () => {
  const { logEntry, isLoading: isMutationLoading } = useManualEntryOperations();
  const userCurrencyCode = useCurrencyCode();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] =
    useState<ManualEntryFormSchema | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm<ManualEntryFormSchema>({
    resolver: yupResolver(manualEntrySchema) as any,
    defaultValues: {
      start_date: new Date().toISOString().split('T')[0] || '',
      end_date: new Date().toISOString().split('T')[0] || '',
      service_type: 'electricidad',
      consumption: 0,
      consumption_unit: getUnit('electricidad'),
      notes: '',
      cost_total: 0,
      currency: userCurrencyCode, // Auto-populated from user profile
      vendor_name: '',
      invoice_number: '',
      issued_date: '',
      supply_id: '',
      bag_size: 'medium',
    },
  });

  const watchedServiceType = watch('service_type');
  const consumptionUnit = watch('consumption_unit');
  const bagSize = watch('bag_size');
  const consumptionValue = watch('consumption');
  const bagCount =
    typeof consumptionValue === 'number'
      ? consumptionValue
      : Number(consumptionValue || 0);
  const availableUnits = getAvailableUnits(watchedServiceType);
  const isBagUnit = consumptionUnit === 'bolsas';
  const bagKilogramsPreview =
    isBagUnit && bagSize ? calculateBagKilograms(bagCount || 0, bagSize) : null;
  const showBagPreview = Boolean(isBagUnit && bagSize && bagCount > 0);

  // Update consumption unit when service type changes
  useEffect(() => {
    if (watchedServiceType) {
      setValue('consumption_unit', getUnit(watchedServiceType));
    }
  }, [watchedServiceType, setValue]);

  useEffect(() => {
    if (consumptionUnit === 'bolsas' && !bagSize) {
      setValue('bag_size', 'medium');
    }
  }, [consumptionUnit, bagSize, setValue]);

  // Update currency when user's currency changes
  useEffect(() => {
    if (userCurrencyCode) {
      setValue('currency', userCurrencyCode);
    }
  }, [userCurrencyCode, setValue]);

  const buildSubmissionPayload = (
    data: ManualEntryFormSchema,
  ): ManualConsumptionEntryPayload | BagWastePayloadWithUnit => {
    const sanitizeText = (value?: string) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : undefined;
    };

    const sanitizePositiveNumber = (value?: number) =>
      typeof value === 'number' && value > 0 ? value : undefined;

    const sanitizedNotes = sanitizeText(data.notes);
    const sanitizedCostTotal = sanitizePositiveNumber(data.cost_total);
    const sanitizedCurrency = sanitizeText(data.currency);
    const sanitizedVendorName = sanitizeText(data.vendor_name);
    const sanitizedInvoiceNumber = sanitizeText(data.invoice_number);
    const sanitizedIssuedDate = sanitizeText(data.issued_date);
    const sanitizedSupplyId = sanitizeText(data.supply_id);

    const sanitizedFields: Partial<
      Pick<
        ManualConsumptionEntryPayload,
        | 'notes'
        | 'cost_total'
        | 'currency'
        | 'vendor_name'
        | 'invoice_number'
        | 'issued_date'
        | 'supply_id'
      >
    > = {
      ...(sanitizedNotes && { notes: sanitizedNotes }),
      ...(sanitizedCostTotal !== undefined && {
        cost_total: sanitizedCostTotal,
      }),
      ...(sanitizedCurrency && { currency: sanitizedCurrency }),
      ...(sanitizedVendorName && { vendor_name: sanitizedVendorName }),
      ...(sanitizedInvoiceNumber && { invoice_number: sanitizedInvoiceNumber }),
      ...(sanitizedIssuedDate && { issued_date: sanitizedIssuedDate }),
      ...(sanitizedSupplyId && { supply_id: sanitizedSupplyId }),
    };

    if (
      data.consumption_unit === 'bolsas' &&
      data.bag_size &&
      isWasteServiceType(data.service_type)
    ) {
      const bagPayload: BagWastePayloadWithUnit = {
        service_type: data.service_type,
        bags: data.consumption,
        bag_size: data.bag_size,
        start_date: data.start_date,
        end_date: data.end_date,
        consumption_unit: 'bolsas',
        ...sanitizedFields,
      };
      return bagPayload;
    }

    const normalizedUnit =
      data.consumption_unit === 'bolsas'
        ? 'bolsas'
        : (getUnit(data.service_type) ?? data.consumption_unit);

    const manualPayload: ManualConsumptionEntryPayload = {
      start_date: data.start_date,
      end_date: data.end_date,
      service_type: data.service_type,
      consumption: data.consumption,
      consumption_unit: normalizedUnit,
      ...sanitizedFields,
    };
    return manualPayload;
  };

  const handleFormSubmit = (data: ManualEntryFormSchema) => {
    setSubmittedData(data);
    setShowConfirmation(true);
  };

  const handleConfirmSubmission = async () => {
    if (!submittedData) return;

    try {
      const payload = buildSubmissionPayload(submittedData);
      await logEntry(payload);
      toast.success('Entrada manual registrada exitosamente');

      // Reset form and close modal
      setTimeout(() => {
        reset();
        setShowConfirmation(false);
        setSubmittedData(null);
      }, 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error logging manual entry:', error);
      toast.error('Error al registrar la entrada. Inténtalo de nuevo.');
    }
  };

  const handleCancelSubmission = () => {
    setShowConfirmation(false);
    setSubmittedData(null);
  };

  const handleReset = () => {
    reset();
    toast.success('Formulario reiniciado');
  };

  return (
    <TabContainer>
      <Card>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Date Range Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="calendar" size="sm" />
              Periodo de Facturación
            </SectionTitle>
            <DateRangeContainer>
              <FormField
                label="Fecha de inicio"
                name="start_date"
                type="date"
                register={register}
                error={errors.start_date}
                required
              />
              <FormField
                label="Fecha de fin"
                name="end_date"
                type="date"
                register={register}
                error={errors.end_date}
                required
              />
            </DateRangeContainer>
          </FormSection>

          {/* Service & Consumption Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="gas" size="sm" />
              Consumo y Servicio
            </SectionTitle>
            <ConsumptionFields>
              <ServiceTypeSelect
                name="service_type"
                control={control}
                label="Tipo de servicio"
                required
                {...(errors.service_type && { error: errors.service_type })}
              />

              {availableUnits.length > 1 && (
                <div>
                  <SectionTitle as="h3">
                    <Icon name="layers" size="sm" aria-hidden /> Unidad de
                    Registro
                  </SectionTitle>
                  <UnitToggleGroup
                    role="radiogroup"
                    aria-label="Unidad de consumo"
                  >
                    {availableUnits.map((unitOption) => {
                      const selected = consumptionUnit === unitOption;
                      const label =
                        unitOption === 'bolsas'
                          ? 'Bolsas'
                          : `Kilogramos (${unitOption})`;
                      return (
                        <UnitToggleOption key={unitOption} $selected={selected}>
                          <input
                            type="radio"
                            value={unitOption}
                            {...register('consumption_unit')}
                          />
                          {label}
                        </UnitToggleOption>
                      );
                    })}
                  </UnitToggleGroup>
                  {errors.consumption_unit && (
                    <ErrorText>{errors.consumption_unit.message}</ErrorText>
                  )}
                </div>
              )}

              {isBagUnit && (
                <div>
                  <SectionTitle as="h3">
                    <Icon name="bag" size="sm" aria-hidden /> Tamaño de bolsa
                  </SectionTitle>
                  <BagSizeGrid>
                    {BAG_SIZE_OPTIONS.map((option) => (
                      <BagSizeCard
                        key={option.value}
                        $selected={bagSize === option.value}
                      >
                        <input
                          type="radio"
                          value={option.value}
                          {...register('bag_size')}
                        />
                        <strong>{option.label}</strong>
                        <span>{option.description}</span>
                      </BagSizeCard>
                    ))}
                  </BagSizeGrid>
                  {errors.bag_size && (
                    <ErrorText>{errors.bag_size.message}</ErrorText>
                  )}
                </div>
              )}

              <FormField
                label={
                  isBagUnit
                    ? 'Cantidad de bolsas'
                    : `Consumo en ${getUnit(watchedServiceType)}`
                }
                name="consumption"
                type="number"
                step={isBagUnit ? 1 : 0.01}
                min={isBagUnit ? '1' : '0'}
                register={register}
                error={errors.consumption}
                placeholder={isBagUnit ? 'Ej: 3' : 'Ej: 125.50'}
                helperText={
                  isBagUnit
                    ? 'Número total de bolsas utilizadas en el período'
                    : 'Ingresa el valor correspondiente al servicio'
                }
                required
              />

              {showBagPreview && bagSize && (
                <BagPreviewCard role="status" aria-live="polite">
                  <BagPreviewTitle>Conversión estimada</BagPreviewTitle>
                  <p style={{ margin: 0 }}>
                    {formatBagQuantity(bagCount, bagSize)} ≈{' '}
                    <strong>
                      {formatNumberEs(bagKilogramsPreview ?? 0, 2)} kg
                    </strong>
                  </p>
                </BagPreviewCard>
              )}
            </ConsumptionFields>
          </FormSection>

          {/* Optional Information Section */}
          <FormSection>
            <OptionalSection>
              <OptionalHeader>
                <Icon name="info" size="sm" />
                <OptionalTitle>Información Adicional (Opcional)</OptionalTitle>
              </OptionalHeader>
              <OptionalFields>
                <FormField
                  label="Proveedor"
                  name="vendor_name"
                  type="text"
                  register={register}
                  error={errors.vendor_name}
                  placeholder="Ej: Iberdrola"
                />
                <FormField
                  label="Número de factura"
                  name="invoice_number"
                  type="text"
                  register={register}
                  error={errors.invoice_number}
                  placeholder="Ej: FAC-2024-001"
                />
                <FormField
                  label="Costo total"
                  name="cost_total"
                  type="number"
                  step="0.01"
                  min="0"
                  register={register}
                  error={errors.cost_total}
                  placeholder="Ej: 89.50"
                />
                {/* Currency is now auto-populated from user profile */}
                <FormField
                  label="Fecha de emisión"
                  name="issued_date"
                  type="date"
                  register={register}
                  error={errors.issued_date}
                />
                <FormField
                  label="ID de suministro"
                  name="supply_id"
                  type="text"
                  register={register}
                  error={errors.supply_id}
                  placeholder="Ej: CUPS o código de contrato"
                />
                <FullWidthField>
                  <FormField
                    label="Notas adicionales"
                    name="notes"
                    type="text"
                    register={register}
                    error={errors.notes}
                    placeholder="Añade cualquier información adicional relevante..."
                  />
                </FullWidthField>
              </OptionalFields>
            </OptionalSection>
          </FormSection>

          {/* Form Actions */}
          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={handleReset}
              disabled={isMutationLoading}
            >
              <Icon name="refresh" size="sm" />
              Reiniciar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isMutationLoading}
              loading={isMutationLoading}
            >
              <Icon name="save" size="sm" />
              {isMutationLoading ? 'Guardando...' : 'Registrar Entrada'}
            </Button>
          </FormActions>
        </form>
      </Card>

      {/* Confirmation Modal */}
      {showConfirmation && submittedData && (
        <EntryConfirmationModal
          isVisible={showConfirmation}
          onClose={handleCancelSubmission}
          onConfirm={handleConfirmSubmission}
          onEdit={handleCancelSubmission}
          isLoading={isMutationLoading}
          data={submittedData}
        />
      )}
    </TabContainer>
  );
};
